import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useTheme,
} from '@chakra-ui/react';
import { Chart } from '../../../charts';
import {
  formatDateToReadableString,
  getLocalDateStr,
  splitTime,
} from '../../../shared/utils';
import { useDetailChartsGql } from '../../graphql';
import {
  Count,
  Label,
  MetricLabelWrapper,
} from '../../helpers/metricsCard.styled';
import { useEffect, useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import AppTooltip from '../../../appTooltip';
import { BsGear } from 'react-icons/bs';
import SetGoalModal from './SetGoalModal';
import { Link, useNavigate } from 'react-router-dom';
import ViewSwitcher from '../../../viewSwitcher';
import { FaBullseye } from 'react-icons/fa';
import { useCallback, useRef } from 'react';
import { toPng } from 'html-to-image';
import Logo from '../../../shared/assets/DD-Logo.svg';
import ImageDown from '../../../shared/assets/image-down.svg';
import { useToastHook } from '../../../appToast';
import { Loader } from '../../../appLoader';
import {
  getViewForFilter,
  viewSwitchObj,
} from '../../helpers/metricCard.utils';
import { ForecastIcon, StreamLineIcon } from '../../../icons';
import { useDashboardStore } from '../../../hooks/useDashboardStore';
import useQueryState from '../../../hooks/useQueryState';
import { getOrgId, getRole, UserRole } from '@devd-client/api';
import { DateRangeSelection, PeriodType } from '../../../shared/typings';

interface MetricDashboardProps {
  selected?: DateRangeSelection;
  selectedTeam: string;
  ticketType?: string;
  setType: (type: string) => void;
  type?: string;
  repos?: string[];
  queryKey: string;
  showForecast?: string;
  setShowForecast?: (showForecast: string) => void;
}

const ignoreToggleArr = [
  'TOP_REVIEWER_DISTRIBUTION',
  'PR_AGEING_DISTRIBUTION',
  'REVIEW_RESPONSIVENESS_INDEX',
  'ISSUE_CYCLE_TIME_DIST',
  'CHANGE_AGING_DISTRIBUTION',
  'ISSUE_PROGRESSION_RATE',
  'ISSUE_LIFECYCLE_DIST',
];

export const MetricDashboard = ({
  selected,
  selectedTeam,
  ticketType,
  type,
  repos,
  queryKey,
  showForecast,
  setShowForecast,
}: MetricDashboardProps) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const setGoalPopup = useDisclosure();
  const [isHovered, setIsHovered] = useState(false);
  const [viewToggle, setViewToggle] = useState('Week');
  const [showControlChart, setShowControlChart] =
    useQueryState('showControlChart');
  const {
    selected: selectedDate,
    setSelected,
    setFilterTitle,
    setActive,
  } = useDashboardStore();
  const [newToast] = useToastHook();
  const [granularity, setGranularity] = useQueryState('granularity');
  const [controlChart, setControlChart] = useState(false);

  useEffect(() => {
    if (granularity === 'day') setViewToggle('Day');
    else if (granularity === 'week') setViewToggle('Week');
    else if (granularity === 'month') setViewToggle('Month');
  }, [granularity]);

  useEffect(() => {
    showControlChart &&
      setControlChart(showControlChart === 'true' ? true : false);
  }, []);

  useEffect(() => {
    if (type === 'PR_CYCLE_TIME_BREAKDOWN') {
      setShowControlChart(controlChart);
    }
  }, [controlChart]);

  useEffect(() => {
    const today = getLocalDateStr();
    if (
      type &&
      (type !== 'ISSUE_THROUGHPUT' || selectedDate.endDate !== today)
    ) {
      setShowForecast && setShowForecast('false');
    }
  }, [type, selectedDate]);

  const { data, isFetching } = useDetailChartsGql(
    selected?.startDate ?? '',
    selected?.endDate ?? '',
    (type as string) === 'PR_CYCLE_TIME_BREAKDOWN' &&
      (showControlChart === true || showControlChart === 'true')
      ? 'PR_CYCLE_TIME'
      : (type as string),
    '',
    selectedTeam,
    selected?.sprintId ?? '',
    ticketType || '',
    repos || [],
    viewToggle.toUpperCase(),
    getViewForFilter(type as string, 'dashboard'),
    showForecast === 'true',
    showControlChart
  );

  const modifiedData = ['PR_CYCLE_TIME'].includes(data?.chartMetadata?.chartKey)
    ? {
        ...data,
        average:
          data?.average && data?.chartMetadata?.dataType == 'INTEGER'
            ? data?.average
            : data?.average && data?.chartMetadata?.dataType == 'TIME'
            ? splitTime(parseInt(data?.average) / 60)
            : data?.average && data?.chartMetadata?.dataType == 'PERCENTAGE'
            ? data?.average
            : data?.average && splitTime(parseInt(data?.average) / 60),
        chartMetadata: {
          ...data.chartMetadata,
          ylabel:
            data?.chartMetadata?.chartKey === 'CUSTOM'
              ? data?.chartMetadata.ylabel
              : 'Hour',
        },
        data: data?.data?.map((item: any) => ({
          ...item,
          y:
            data?.chartMetadata?.chartKey === 'CUSTOM'
              ? Number(item.y)
              : Number(item.y) / 60,
          clusterP90ToP95: Number(item.clusterP90ToP95) / 60 || 0,
          clusterP95ToP100: Number(item.clusterP95ToP100) / 60 || 0,
          clusterP0ToP5: Number(item.clusterP0ToP5) / 60 || 0,
          clusterP5ToP10: Number(item.clusterP5ToP10) / 60 || 0,
        })),
        keys: data?.keys?.map((key: any) =>
          key.name === 'Minutes' ? { ...key, name: 'Hours' } : key
        ),
        refLines: data?.refLines,
      }
    : ['LEAD_TIME_BREAKDOWN'].includes(data?.chartMetadata?.chartKey)
    ? {
        ...data,
        chartMetadata: { ...data?.chartMetadata, ylabel: 'Hour' },
        data: data?.data?.map((item: any) => ({
          ...item,
          codingTime: Number(item.codingTime) / 60,
          deployTime: Number(item.deployTime) / 60,
          mergeTime: Number(item.mergeTime) / 60,
          pickupTime: Number(item.pickupTime) / 60,
        })),
        keys: data?.keys?.map((key: any) =>
          key.name === 'Minutes' ? { ...key, name: 'Hours' } : key
        ),
      }
    : ['MEAN_TIME_TO_RECOVERY_DISTRIBUTION'].includes(
        data?.chartMetadata?.chartKey
      )
    ? {
        ...data,
        chartMetadata: { ...data?.chartMetadata, ylabel: 'Hour' },
        data: data?.data?.map((item: any) => ({
          ...item,
          High: item.High
            ? parseFloat(String(Number(item.High) / 60)).toFixed(1)
            : 0,
          Low: item.Low
            ? parseFloat(String(Number(item.Low) / 60)).toFixed(1)
            : 0,
          Lowest: item.Lowest
            ? parseFloat(String(Number(item.Lowest) / 60)).toFixed(1)
            : 0,
          Medium: item.Medium
            ? parseFloat(String(Number(item.Medium) / 60)).toFixed(1)
            : 0,
          Critical: item.Critical
            ? parseFloat(String(Number(item.Critical) / 60)).toFixed(1)
            : 0,
          Highest: item.Highest
            ? parseFloat(String(Number(item.Highest) / 60)).toFixed(1)
            : 0,
        })),
        keys: data?.keys?.map((key: any) =>
          key.name === 'Minutes' ? { ...key, name: 'Hours' } : key
        ),
      }
    : ['ISSUE_LIFECYCLE_DIST'].includes(data?.chartMetadata?.chartKey) &&
      getOrgId() === 'SYMYCOXHO'
    ? {
        ...data,
        data: data?.data?.map((item: any) => ({
          ...item,
          label: item.label,
          issueCount: parseFloat(item.issueCount),
          cycleTime: parseFloat(item.cycleTime),
        })),
      }
    : ['ISSUE_LIFECYCLE_DIST'].includes(data?.chartMetadata?.chartKey)
    ? {
        ...data,
        data: data?.data?.map((item: any) => ({
          ...item,
          label: item.label,
          value: parseFloat(item.value),
          avgValue: parseFloat(item.avgValue),
        })),
      }
    : data;

  const [show, setShow] = useState(true);

  const ref = useRef<HTMLDivElement>(null);
  const [mergedImage, setMergedImage] = useState<string | null>(null);
  const [imageDownloadLoading, setImageDownloadLoading] = useState(false);

  const downloadChart = useCallback(
    (chartName: string) => {
      if (ref.current === null) {
        return;
      }

      setImageDownloadLoading(true);

      toPng(ref.current, { cacheBust: true })
        .then((dataUrl) => {
          const img = document.createElement('img') as HTMLImageElement;
          img.src = dataUrl;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            if (!context) return;
            canvas.width = img.width;
            canvas.height = img.height;

            context.drawImage(img, 0, 0);
            const logoImg = document.createElement('img') as HTMLImageElement;
            logoImg.src = Logo;
            logoImg.onload = () => {
              const logoWidth = logoImg.width / 4;
              const logoHeight = logoImg.height / 4;
              const logoX = canvas.width - logoWidth - 17;
              const logoY = 90;

              context.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

              const mergedDataUrl = canvas.toDataURL('image/png');
              setMergedImage(mergedDataUrl);

              const link = document.createElement('a');
              link.download = `${chartName}.png`;
              link.href = mergedDataUrl;
              link.click();
              setImageDownloadLoading(false);
              setShow(true);
            };
          };
          newToast({
            message: 'Chart downloaded successfully.',
            status: 'success',
          });
        })
        .catch((err) => {
          setShow(true);
          setImageDownloadLoading(false);
          newToast({
            message: 'Something went wrong while downloading.',
            status: 'error',
          });
        });
    },
    [ref]
  );

  return (
    <>
      <Box
        borderRadius="4px"
        border="0.5px solid rgba(0, 0, 0, 0.1)"
        boxShadow="md"
        bg={'white'}
        ref={ref}
      >
        <Box mx={3} mt={2} mb={3}>
          <Flex w="full" justify={'space-between'}>
            <Box w="100%">
              <Flex align="center" justify="space-between">
                <Flex align="center">
                  <Text
                    as="span"
                    fontSize="lg"
                    color="text.secondary"
                    fontWeight="semibold"
                    fontFamily="heading"
                    mr={1}
                    minW={'max-content'}
                  >
                    {modifiedData?.chartMetadata?.chartTitle}
                  </Text>
                  {modifiedData?.chartMetadata?.info && (
                    <AppTooltip
                      content={
                        <Text
                          fontSize="sm"
                          color="text.secondary"
                          fontWeight="normal"
                          fontFamily="heading"
                        >
                          {modifiedData?.chartMetadata?.info}
                        </Text>
                      }
                      placement="bottom"
                      trigger="hover"
                    >
                      <MdInfoOutline size={16} color={colors.text.secondary} />
                    </AppTooltip>
                  )}
                </Flex>
                {modifiedData?.chartDataState === 'NOT_CONFIGURED' ||
                modifiedData?.chartDataState === 'NO_INTEGRATION' ? (
                  <AppTooltip
                    trigger="hover"
                    content={
                      <Flex>
                        <Link to={`${modifiedData?.chartError?.link}`}>
                          <Text
                            color="primary"
                            fontFamily="heading"
                            fontSize="sm"
                            textDecoration={'underline'}
                          >
                            Configure
                          </Text>
                        </Link>
                        <Text
                          fontFamily="heading"
                          fontSize="sm"
                          color="text.primary"
                          ml={2}
                        >
                          {modifiedData?.chartError?.message}
                        </Text>
                      </Flex>
                    }
                  >
                    <Box>
                      <Button
                        size={'sm'}
                        color={'error'}
                        border="1px"
                        borderColor={'error'}
                        backgroundColor={'white'}
                        fontFamily="heading"
                      >
                        {'why no data?'}
                      </Button>
                    </Box>
                  </AppTooltip>
                ) : (
                  <Flex align="center" gap={2}>
                    {(type === 'PR_CYCLE_TIME_BREAKDOWN' ||
                      type === 'PR_CYCLE_TIME') && (
                      <AppTooltip
                        content={
                          !controlChart ? (
                            <Text
                              fontFamily={'heading'}
                              color="text.secondary2"
                            >
                              <Text as="span" fontWeight={'semibold'}>
                                Enable Outlier Detection:
                              </Text>{' '}
                              Identify critical outliers and track true trends.
                              This control chart uses percentiles to highlight
                              unusual data. Focus on P90/P95 for metrics that
                              improve when low, and P10/P5 for metrics that
                              improve when high. A moving average, unaffected by
                              outliers, is also displayed.
                            </Text>
                          ) : (
                            <Text
                              fontFamily={'heading'}
                              color="text.secondary2"
                            >
                              Disable Outlier Detection
                            </Text>
                          )
                        }
                        trigger="hover"
                      >
                        <IconButton
                          size="xs"
                          aria-label="streamLine"
                          icon={
                            <StreamLineIcon
                              color={controlChart ? '#4D4DFF' : '#626266'}
                            />
                          }
                          border="1px solid"
                          borderColor={
                            controlChart ? 'primary' : 'rgba(0,0,0,0.1)'
                          }
                          bg="transperant"
                          onClick={() => setControlChart(!controlChart)}
                        />
                      </AppTooltip>
                    )}
                    {!ignoreToggleArr.includes(
                      modifiedData?.chartMetadata?.chartKey
                    ) && (
                      <ViewSwitcher
                        tabList={viewSwitchObj}
                        currentTab={viewToggle}
                        handleSwitch={setViewToggle}
                        height="25px"
                      />
                    )}

                    {modifiedData?.chartMetadata?.chartKey ===
                    'ISSUE_THROUGHPUT' ? (
                      <Button
                        size="xs"
                        color={
                          showForecast === 'true' ? 'primary' : 'text.secondary'
                        }
                        border="1px solid"
                        borderRadius={'4px'}
                        borderColor={
                          showForecast === 'true' ? 'primary' : 'text.secondary'
                        }
                        bg={
                          showForecast === 'true' ? '#4D4DFF1A' : 'transparent'
                        }
                        onClick={() => {
                          setShowForecast &&
                            setShowForecast(
                              showForecast === 'true' ? 'false' : 'true'
                            );
                          const today = getLocalDateStr();
                          if (selectedDate.endDate !== today) {
                            const thirtyDaysAgo = new Date();
                            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                            const formattedThirtyDaysAgo =
                              getLocalDateStr(thirtyDaysAgo);

                            setSelected({
                              ...(selected as PeriodType),
                              startDate: formattedThirtyDaysAgo,
                              endDate: today,
                              sprintId: '',
                              sprintName: '',
                              duration: '1M',
                            });
                            setFilterTitle(
                              `${formatDateToReadableString(
                                formattedThirtyDaysAgo
                              )} - ${formatDateToReadableString(today)}`
                            );
                            setActive('1M');
                          }
                        }}
                      >
                        <Tooltip
                          label={
                            showForecast === 'true'
                              ? 'Disable Forecast'
                              : 'Enable Forecast'
                          }
                          placement="bottom"
                          hasArrow
                        >
                          <Box>
                            <ForecastIcon
                              color={
                                showForecast === 'true'
                                  ? colors.primary
                                  : colors.text.secondary
                              }
                            />
                          </Box>
                        </Tooltip>
                      </Button>
                    ) : null}

                    {getRole() !== UserRole.EMPLOYEE && (
                      <Button
                        size="xs"
                        color="primary"
                        variant="primary"
                        onClick={setGoalPopup.onOpen}
                        leftIcon={<FaBullseye />}
                      >
                        Set Goal
                      </Button>
                    )}
                    {modifiedData?.configLink && (
                      <Button
                        as={Link}
                        size="xs"
                        color="primary"
                        variant="primary"
                        to={modifiedData?.configLink}
                        leftIcon={<BsGear />}
                      >
                        Configure
                      </Button>
                    )}
                  </Flex>
                )}
              </Flex>

              {typeof modifiedData?.average !== 'undefined' &&
                modifiedData?.average !== null && (
                  <MetricLabelWrapper>
                    {typeof modifiedData?.average !== 'undefined' &&
                      modifiedData?.average !== null && (
                        <Count>
                          {String(modifiedData?.average)}{' '}
                          {modifiedData?.chartMetadata?.dataType ===
                            'PERCENTAGE' && (
                            <Text fontSize={'18px'} display={'inline'}>
                              %
                            </Text>
                          )}
                        </Count>
                      )}
                    <Label>
                      {modifiedData?.chartMetadata?.dataType !== 'PERCENTAGE' &&
                        modifiedData?.averageLabel}
                    </Label>
                  </MetricLabelWrapper>
                )}

              {modifiedData?.threshold && (
                <MetricLabelWrapper>
                  <Count
                    color={
                      modifiedData?.threshold?.color || colors.text.primary
                    }
                  >
                    {modifiedData?.threshold === null
                      ? '_'
                      : modifiedData?.threshold?.value === 'NaN'
                      ? '_'
                      : modifiedData?.threshold?.value === null
                      ? '_'
                      : modifiedData?.chartMetadata?.chartKey ===
                          'PR_CYCLE_TIME_BREAKDOWN' ||
                        modifiedData?.chartMetadata?.chartKey ===
                          'LEAD_TIME_BREAKDOWN' ||
                        modifiedData?.chartMetadata?.chartKey ===
                          'MEAN_TIME_TO_RECOVERY_DISTRIBUTION'
                      ? splitTime(parseFloat(modifiedData?.threshold?.value))
                      : String(
                          parseFloat(modifiedData?.threshold?.value).toFixed(1)
                        )}
                  </Count>
                  <Label>{modifiedData?.threshold?.title}</Label>
                </MetricLabelWrapper>
              )}
              <Flex
                justify={'space-between'}
                align="flex-end"
                marginBottom="20px"
              >
                <Text
                  w="50%"
                  color="text.secondary"
                  fontFamily="heading"
                  fontSize="sm"
                >
                  {modifiedData?.threshold?.desc}
                </Text>

                {!isFetching && show && modifiedData?.data?.length > 0 && (
                  <Box>
                    <Tooltip label="Download Chart" hasArrow>
                      <Button
                        variant={'unstyled'}
                        size={'xs'}
                        mx={2}
                        zIndex={99}
                        onClick={() => {
                          setShow(false);
                          downloadChart(
                            modifiedData?.chartMetadata?.chartTitle ?? 'Chart'
                          );
                        }}
                      >
                        <Image w={5} h={5} src={ImageDown} />
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Flex>
            </Box>
          </Flex>

          {isFetching ? (
            <Box position={'relative'}>
              <Skeleton
                height="300px"
                width="100%"
                style={{ marginTop: '20px' }}
              />
              <Spinner position={'absolute'} top={'45%'} left={'47%'} />
            </Box>
          ) : modifiedData?.chartDataState === 'NO_INTEGRATION' ? (
            <Box
              w="full"
              height="300px"
              display="flex"
              justifyContent={'center'}
              alignItems="center"
              position="relative"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Image
                height="100%"
                src={modifiedData?.chartMetadata?.zsImage || ''}
                style={{ position: 'relative' }}
              />
              {isHovered && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  backgroundColor="rgba(0, 0, 0, 0.4)"
                  borderRadius="4px"
                >
                  {modifiedData?.chartMetadata?.source && (
                    <Button
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      onClick={() => navigate('/settings/integrations')}
                    >
                      Connect{' '}
                      {modifiedData?.chartMetadata?.source === 'ISSUE TRACKING'
                        ? 'Jira'
                        : modifiedData?.chartMetadata?.source === 'REPOSITORY'
                        ? 'Github'
                        : ''}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box
              w="100%"
              height="300px"
              marginTop="20px"
              overflowY={'auto'}
              overflowX={'hidden'}
              position={'relative'}
              onMouseEnter={() => {
                setIsHovered(true);
              }}
              onMouseLeave={() => {
                setIsHovered(false);
              }}
            >
              {modifiedData?.chartMetadata?.chartKey ===
              'TOP_REVIEWER_DISTRIBUTION' ? (
                <Box
                  height={
                    modifiedData?.data?.length <= 6
                      ? '300px'
                      : modifiedData?.data?.length > 6 &&
                        modifiedData?.data?.length <= 10
                      ? '500px'
                      : modifiedData?.data?.length > 10 &&
                        modifiedData?.data?.length <= 20
                      ? '800px'
                      : modifiedData?.data?.length > 20 &&
                        modifiedData?.data?.length <= 37
                      ? '1000px'
                      : `${modifiedData?.data?.length * 25}`
                    // the above is just to scale the number or entries rather than having a fixed size.
                  }
                >
                  <Chart
                    data={modifiedData?.data}
                    keys={modifiedData?.keys}
                    chartMetadata={modifiedData?.chartMetadata}
                    showLabel={true}
                    forDetails={true}
                    selectedTeam={selectedTeam}
                    selected={selected}
                    ticketType={ticketType}
                    repos={repos}
                  />
                </Box>
              ) : (
                <Chart
                  refLines={modifiedData?.refLines}
                  data={modifiedData?.data}
                  keys={modifiedData?.keys}
                  chartMetadata={modifiedData?.chartMetadata}
                  showLabel={true}
                  forDetails={true}
                  granularSlant={
                    viewToggle === 'Day' ||
                    modifiedData?.chartMetadata.chartKey ===
                      'ISSUE_LIFECYCLE_DIST'
                  }
                  selectedTeam={selectedTeam}
                  selected={selected}
                  ticketType={ticketType}
                  repos={repos}
                  showYLabel
                  showXLabel
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      {imageDownloadLoading && <Loader />}
      {setGoalPopup.isOpen && (
        <SetGoalModal
          isOpen={setGoalPopup.isOpen}
          onClose={setGoalPopup.onClose}
          metricTitle={modifiedData?.chartMetadata?.chartTitle}
          queryKey={queryKey}
        />
      )}
    </>
  );
};

export default MetricDashboard;
